import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IFolhaCalendario, IFolhaEmpregado, IFolhaLinhaOpcao, IFolhaVisao } from '../../interfaces/IModuloBancario';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import { BarraFiltros, BuscaOpcoes, PaginacaoModulo, useFiltroLista } from './paginacao-modulo';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = nenhuma competência escolhida. */
    competencia: string;
    /** A geração das minutas muda a situação dos empregados. */
    versaoMinutas: number;
    /** Ler a folha, mudar o calendário ou vincular muda as minutas. */
    onAlterou: () => void;
}

const INTERVALO_MS = 4000;

const SITUACAO_ARQUIVO: Record<string, { texto: string; classe: string }> = {
    ANALISANDO: { texto: 'Lendo', classe: 'badge-light-primary' },
    LIDO: { texto: 'Lido', classe: 'badge-light-success' },
    DUPLICADO: { texto: 'Já enviado', classe: 'badge-light' },
    ERRO: { texto: 'Erro', classe: 'badge-light-danger' },
};

const STATUS: Record<string, { texto: string; classe: string }> = {
    RECUSADO: { texto: 'Recusado', classe: 'badge-light-danger' },
    ABERTO: { texto: 'Sem débito no extrato', classe: 'badge-light-warning' },
    PENDENTE: { texto: 'Pendente', classe: 'badge-light-warning' },
    EM_MINUTA: { texto: 'Em minuta', classe: 'badge-light-primary' },
    PARCIAL: { texto: 'Liquidado em parte', classe: 'badge-light-info' },
    LIQUIDADO: { texto: 'Liquidado', classe: 'badge-light-success' },
};

const TIPO: Record<string, string> = {
    MENSAL: 'Mensal',
    ADIANTAMENTO: 'Adiantamento',
    DECIMO_TERCEIRO: '13º salário',
    FERIAS: 'Férias',
    RESCISAO: 'Rescisão',
    OUTROS: 'Outras',
};

const REGRA: Record<string, string> = {
    QUINTO_DIA_UTIL: '5º dia útil do mês seguinte',
    ULTIMO_DIA: 'Último dia do mês',
};

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const VAZIO: IFolhaVisao = { arquivos: [], empregados: [], calendario: [], totais: { empregados: 0, liquido: 0, liquidado: 0, emMinuta: 0, aberto: 0 }, totalItens: 0, pagina: 1, tamanhoPagina: 50 };
const STATUS_FILTRO = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.texto]));
const LIMITE_EMPREGADOS_MODAL = 200;

// DEVN3-777 — folha analítica: leitura sem custo, liquidação por empregado, calendário e vínculo manual.
export default function FolhaAnalitica({ esteiraId, competencia, versaoMinutas, onAlterou }: Props) {
    const [visao, setVisao] = useState<IFolhaVisao>(VAZIO);
    const [enviando, setEnviando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [calendario, setCalendario] = useState<{ item: IFolhaCalendario; regra: string; inicio: string } | null>(null);
    const [vinculo, setVinculo] = useState<{ linhas: IFolhaLinhaOpcao[]; totalLinhas: number; linha: IFolhaLinhaOpcao | null; empregados: IFolhaEmpregado[]; buscaEmpregado: string; marcados: Record<number, number> } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [0, 0];
    const filtro = useFiltroLista();

    const buscar = useCallback(async (silencioso = false) => {
        if (!ano || !mes) return;
        try {
            setVisao(await ModuloBancarioService.folha(esteiraId, ano, mes, filtro.params));
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? 'Não foi possível carregar a folha.', icon: 'error' });
        }
    }, [esteiraId, ano, mes, filtro.params]);

    // O filtro muda a consulta, não a competência: a lista antiga fica na tela até a nova chegar.
    useEffect(() => {
        setVisao(VAZIO);
    }, [esteiraId, ano, mes]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    useEffect(() => {
        if (versaoMinutas > 0) buscar(true);
    }, [versaoMinutas, buscar]);

    const emAndamento = visao.arquivos.some(a => a.situacao === 'ANALISANDO');
    useEffect(() => {
        if (!emAndamento) return;
        const id = setInterval(() => buscar(true), INTERVALO_MS);
        return () => clearInterval(id);
    }, [emAndamento, buscar]);

    // Terminada a leitura, a geração refaz as minutas: a lista precisa acompanhar.
    const processavaAntes = useRef(false);
    useEffect(() => {
        if (processavaAntes.current && !emAndamento) onAlterou();
        processavaAntes.current = emAndamento;
    }, [emAndamento, onAlterou]);

    const enviar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files || files.length === 0) return;

        setEnviando(true);
        const falhas: string[] = [];
        for (const file of Array.from(files)) {
            try {
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                await ModuloBancarioService.registrarFolha(esteiraId, { ano, mes, nomeGerado: generatedFileName, nomeOriginal: file.name });
            } catch (err: any) {
                falhas.push(`${file.name}: ${err?.message ?? 'falha no envio'}`);
            }
        }
        setEnviando(false);
        if (inputRef.current) inputRef.current.value = '';
        if (falhas.length > 0) Swal.fire({ title: 'Alguns arquivos não foram enviados', html: falhas.join('<br/>'), icon: 'warning' });
        buscar();
    };

    const executar = async (acao: () => Promise<unknown>, erro: string) => {
        setSalvando(true);
        try {
            await acao();
            await buscar(true);
            onAlterou();
            return true;
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? erro, icon: 'error' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const descartar = async (id: number, nome: string, lido: boolean) => {
        if (lido) {
            const { isConfirmed } = await Swal.fire({
                text: `Descartar ${nome}? Os empregados dele saem da lista e as minutas regeneráveis são refeitas.`,
                icon: 'warning', showCancelButton: true, confirmButtonText: 'Descartar', cancelButtonText: 'Cancelar',
            });
            if (!isConfirmed) return;
        }
        await executar(() => ModuloBancarioService.descartarFolha(esteiraId, [id]), 'Não foi possível descartar.');
    };

    const salvarCalendario = async () => {
        if (!calendario) return;
        const ok = await executar(
            () => ModuloBancarioService.salvarCalendarioFolha(esteiraId, { tipoFolha: calendario.item.tipoFolha, regra: calendario.regra, vigenciaInicio: calendario.inicio || null }),
            'Não foi possível salvar o calendário.');
        if (ok) setCalendario(null);
    };

    const abrirVinculo = async () => {
        try {
            const [linhas, empregados] = await Promise.all([
                ModuloBancarioService.linhasDaFolha(esteiraId, ano, mes),
                ModuloBancarioService.empregadosDaFolha(esteiraId, ano, mes),
            ]);
            setVinculo({
                linhas: linhas.itens, totalLinhas: linhas.total, linha: null,
                empregados: empregados.filter(e => e.situacao !== 'RECUSADO'), buscaEmpregado: '', marcados: {},
            });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar as saídas do extrato.', icon: 'error' });
        }
    };

    const buscarLinhas = useCallback(async (busca: string) => {
        try {
            const { itens, total } = await ModuloBancarioService.linhasDaFolha(esteiraId, ano, mes, busca);
            setVinculo(v => (v ? { ...v, linhas: itens, totalLinhas: total } : v));
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível buscar as saídas do extrato.', icon: 'error' });
        }
    }, [esteiraId, ano, mes]);

    const escolherLinha = (extratoId: string) => {
        if (!vinculo) return;
        const linha = vinculo.linhas.find(l => String(l.extratoId) === extratoId) ?? null;
        setVinculo({ ...vinculo, linha, marcados: Object.fromEntries((linha?.vinculados ?? []).map(v => [v.empregadoId, v.valor])) });
    };

    // A busca troca as opções; a saída escolhida continua valendo.
    const linhaEscolhida = vinculo?.linha;
    const opcoesLinha = vinculo
        ? (linhaEscolhida && !vinculo.linhas.some(l => l.extratoId === linhaEscolhida.extratoId) ? [linhaEscolhida, ...vinculo.linhas] : vinculo.linhas)
        : [];
    const termoEmpregado = (vinculo?.buscaEmpregado ?? '').trim().toLowerCase();
    const empregadosCandidatos = (vinculo?.empregados ?? [])
        .filter(e => e.aberto > 0 || vinculo?.marcados[e.id] !== undefined)
        .filter(e => !termoEmpregado || vinculo?.marcados[e.id] !== undefined || (e.nome ?? '').toLowerCase().includes(termoEmpregado));
    const marcar = (id: number, aberto: number, marcado: boolean) => {
        if (!vinculo) return;
        const marcados = { ...vinculo.marcados };
        if (marcado) marcados[id] = aberto; else delete marcados[id];
        setVinculo({ ...vinculo, marcados });
    };
    const somaMarcados = vinculo ? Object.values(vinculo.marcados).reduce((s, v) => s + v, 0) : 0;
    // Em centavos, para a soma de muitos valores não errar a comparação.
    const fecha = !!linhaEscolhida && Math.round(somaMarcados * 100) === Math.round(linhaEscolhida.valor * 100);
    const temVinculoSalvo = (linhaEscolhida?.vinculados.length ?? 0) > 0;

    const salvarVinculo = async () => {
        if (!vinculo || !linhaEscolhida) return;
        const itens = Object.entries(vinculo.marcados).map(([empregadoId, valor]) => ({ empregadoId: Number(empregadoId), valor }));
        const ok = await executar(() => ModuloBancarioService.vincularFolha(esteiraId, linhaEscolhida.extratoId, itens), 'Não foi possível salvar o vínculo.');
        if (ok) setVinculo(null);
    };

    const totais = visao.totais;

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Folha analítica
                    {(enviando || emAndamento) && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                {competencia && (
                    <div className='d-flex align-items-center gap-2'>
                        <input ref={inputRef} type='file' multiple accept='.xlsx,.xls,.csv' className='d-none' onChange={enviar} />
                        {totais.empregados > 0 && (
                            <button className='btn btn-sm btn-light' disabled={salvando} onClick={abrirVinculo}>
                                <i className='fas fa-link'></i> Vincular débito
                            </button>
                        )}
                        <button className='btn btn-sm btn-light-primary' disabled={enviando} onClick={() => inputRef.current?.click()}>
                            <i className='fas fa-upload'></i> Enviar folha
                        </button>
                    </div>
                )}
            </div>
            <div className='card-body pt-0'>
                {!competencia ? (
                    <div className='text-muted'>Escolha a competência no topo da página.</div>
                ) : (
                    <>
                        <div className='text-muted fs-7 mb-3'>
                            XLSX ou CSV com uma linha por empregado e as colunas nome, CPF e líquido. Tipo de folha, banco e lote são opcionais. O débito de qualquer conta bancária da empresa casa com a soma dos líquidos, e a soma por empregado nunca passa do líquido.
                        </div>

                        <div className='table-responsive mb-4'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-1'>
                                <thead>
                                    <tr className='fw-bold text-muted fs-7'>
                                        <th>Tipo de folha</th>
                                        <th>Pagamento</th>
                                        <th>Previsto nesta competência</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visao.calendario.map(c => (
                                        <tr key={c.tipoFolha}>
                                            <td className='fs-7'>{TIPO[c.tipoFolha] ?? c.tipoFolha}</td>
                                            <td className='fs-7'>
                                                {REGRA[c.regra] ?? c.regra}
                                                {c.versao > 0 ? <span className='text-muted fs-8 ms-2'>v{c.versao}</span> : <span className='text-muted fs-8 ms-2'>padrão</span>}
                                            </td>
                                            <td className='fs-7'>{moment(c.dataPrevista).format('DD/MM/YYYY')}</td>
                                            <td className='text-end'>
                                                <button className='btn btn-sm btn-link p-0' disabled={salvando}
                                                    onClick={() => setCalendario({ item: c, regra: c.regra, inicio: '' })}>Alterar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {visao.arquivos.map(a => (
                            <div key={a.id} className='d-flex flex-wrap align-items-center gap-2 fs-7 mb-2'>
                                <span className={`badge ${SITUACAO_ARQUIVO[a.situacao]?.classe ?? 'badge-light'}`}>{SITUACAO_ARQUIVO[a.situacao]?.texto ?? a.situacao}</span>
                                <button className='btn btn-link btn-sm p-0'
                                    onClick={() => ModuloBancarioService.baixarFolha(esteiraId, a.id, a.nomeOriginal)
                                        .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível abrir o arquivo.', icon: 'error' }))}>{a.nomeOriginal}</button>
                                {a.mensagem && <span className='text-muted'>{a.mensagem}</span>}
                                {a.situacao !== 'ANALISANDO' && (
                                    <button className='btn btn-sm btn-link p-0 ms-auto' disabled={salvando}
                                        onClick={() => descartar(a.id, a.nomeOriginal, a.situacao === 'LIDO')}>
                                        {a.situacao === 'LIDO' ? 'Descartar' : 'Tirar da lista'}
                                    </button>
                                )}
                            </div>
                        ))}

                        {(visao.totalItens > 0 || filtro.ativo) && (
                            <BarraFiltros filtro={filtro} placeholder='Buscar por empregado, banco ou lote' situacoes={STATUS_FILTRO} />
                        )}
                        {visao.empregados.length === 0 && filtro.ativo && <div className='text-muted py-4'>Nenhum empregado com estes filtros.</div>}

                        {visao.empregados.length > 0 && (
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mt-3'>
                                    <thead>
                                        <tr className='fw-bold text-muted'>
                                            <th>Empregado</th>
                                            <th>Tipo</th>
                                            <th>Banco / lote</th>
                                            <th className='text-end'>Líquido</th>
                                            <th className='text-end'>Liquidado</th>
                                            <th className='text-end'>Em minuta</th>
                                            <th className='text-end'>Em aberto</th>
                                            <th>Situação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visao.empregados.map(e => (
                                            <tr key={e.id}>
                                                <td className='fs-7'>
                                                    {e.nome ?? '—'}
                                                    {e.cpfMascarado && <div className='text-muted fs-8'>{e.cpfMascarado}</div>}
                                                </td>
                                                <td className='fs-7'>{TIPO[e.tipoFolha] ?? e.tipoFolha}</td>
                                                <td className='fs-7'>{[e.banco, e.lote].filter(Boolean).join(' / ') || '—'}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(e.liquido)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(e.liquidado)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(e.emMinuta)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(e.aberto)}</td>
                                                <td className='fs-7'>
                                                    <span className={`badge ${STATUS[e.situacao]?.classe ?? 'badge-light'}`}>{STATUS[e.situacao]?.texto ?? e.situacao}</span>
                                                    {e.motivo && <div className='text-muted fs-8'>{e.motivo}</div>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className='fw-bold'>
                                            <td colSpan={3} className='fs-7'>{totais.empregados} empregado(s) válidos na competência</td>
                                            <td className='text-end text-nowrap fs-7'>{brl(totais.liquido)}</td>
                                            <td className='text-end text-nowrap fs-7'>{brl(totais.liquidado)}</td>
                                            <td className='text-end text-nowrap fs-7'>{brl(totais.emMinuta)}</td>
                                            <td className='text-end text-nowrap fs-7'>{brl(totais.aberto)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                        <PaginacaoModulo total={visao.totalItens} pagina={filtro.pagina} tamanhoPagina={filtro.tamanhoPagina} onPagina={filtro.setPagina} onTamanho={filtro.setTamanho} />
                    </>
                )}
            </div>

            {calendario && (
                <Modal show onHide={() => setCalendario(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Calendário: {TIPO[calendario.item.tipoFolha] ?? calendario.item.tipoFolha}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-muted fs-7 mb-3'>O calendário define só onde procurar o débito: 5 dias úteis antes e depois da data prevista. Vale até ser alterado, e a versão anterior fica na trilha.</div>
                        <label className='form-label'>Pagamento</label>
                        <select className='form-select mb-3' value={calendario.regra} onChange={e => setCalendario({ ...calendario, regra: e.target.value })}>
                            {Object.entries(REGRA).map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
                        </select>
                        <label className='form-label'>Vale a partir de</label>
                        <input type='date' className='form-control' value={calendario.inicio} onChange={e => setCalendario({ ...calendario, inicio: e.target.value })} />
                        <div className='text-muted fs-8 mt-1'>Em branco, a versão nova substitui a anterior desde o início dela.</div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setCalendario(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando} onClick={salvarCalendario}>Salvar</button>
                    </Modal.Footer>
                </Modal>
            )}

            {vinculo && (
                <Modal show onHide={() => setVinculo(null)} centered size='lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>Vincular débito à folha</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <label className='form-label'>Saída do extrato</label>
                        <BuscaOpcoes placeholder='Buscar a saída pelo histórico' mostradas={vinculo.linhas.length} total={vinculo.totalLinhas} onBuscar={buscarLinhas} />
                        <select className='form-select mb-3' value={linhaEscolhida ? String(linhaEscolhida.extratoId) : ''} onChange={e => escolherLinha(e.target.value)}>
                            <option value=''>Escolha</option>
                            {opcoesLinha.map(l => (
                                <option key={l.extratoId} value={l.extratoId}>{moment(l.data).format('DD/MM/YYYY')} — {l.contaBancaria} — {brl(l.valor)} — {l.historico}</option>
                            ))}
                        </select>
                        {linhaEscolhida && (
                            <>
                                <div className={`fs-7 mb-2 ${fecha ? 'text-success' : 'text-muted'}`}>
                                    Marcados: {brl(somaMarcados)} de {brl(linhaEscolhida.valor)}. A soma precisa fechar com a linha. Para pagamento em parte, informe o valor no empregado.
                                </div>
                                <input className='form-control form-control-sm mb-2' placeholder='Buscar empregado' aria-label='Buscar empregado' value={vinculo.buscaEmpregado}
                                    onChange={e => setVinculo({ ...vinculo, buscaEmpregado: e.target.value })} />
                                {empregadosCandidatos.length > LIMITE_EMPREGADOS_MODAL && (
                                    <div className='text-muted fs-8 mb-2'>Mostrando {LIMITE_EMPREGADOS_MODAL} de {empregadosCandidatos.length.toLocaleString('pt-BR')}. Refine a busca para ver os demais.</div>
                                )}
                                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                    {empregadosCandidatos.slice(0, LIMITE_EMPREGADOS_MODAL).map(e => {
                                        const marcado = vinculo.marcados[e.id] !== undefined;
                                        return (
                                            <div key={e.id} className='d-flex align-items-center gap-2 py-1 fs-7'>
                                                <input type='checkbox' className='form-check-input' checked={marcado} onChange={ev => marcar(e.id, e.aberto, ev.target.checked)} />
                                                <span className='flex-grow-1'>{e.nome} <span className='text-muted'>{TIPO[e.tipoFolha]} · em aberto {brl(e.aberto)}</span></span>
                                                {marcado && (
                                                    <input type='number' step='0.01' min='0.01' className='form-control form-control-sm w-125px' value={vinculo.marcados[e.id]}
                                                        onChange={ev => setVinculo({ ...vinculo, marcados: { ...vinculo.marcados, [e.id]: Number(ev.target.value) } })} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setVinculo(null)}>Cancelar</button>
                        {temVinculoSalvo && Object.keys(vinculo.marcados).length === 0
                            ? <button className='btn btn-danger' disabled={salvando} onClick={salvarVinculo}>Desfazer vínculo</button>
                            : <button className='btn btn-primary' disabled={salvando || !fecha} onClick={salvarVinculo}>Salvar</button>}
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}
